/**
 * Il giro dei valori nelle impostazioni del telefono.
 *
 * ## Perché un giro, e perché la regola sta qui
 *
 * Sul televisore ogni voce a più valori si apre in un elenco da percorrere col telecomando. Su un
 * telefono quella forma costerebbe un passaggio in più per cambiare un valore fra tre: si tocca la
 * riga e si passa al successivo, tornando al primo dopo l'ultimo.
 *
 * La regola è **quale sia il successivo**, e sta nel dominio perché è l'unica parte che si può
 * sbagliare: un giro che non si chiude lascia l'ultimo valore senza via d'uscita, e nessuno se ne
 * accorge finché qualcuno non arriva in fondo. Qui si prova senza montare niente.
 *
 * ## `auto` è un valore, non l'assenza di un valore
 *
 * La lingua gira fra `auto` e le sette tradotte. Toglierlo dal giro renderebbe impossibile **tornare**
 * alla lingua di sistema dopo averne scelta una a mano — una porta che si apre in un verso solo.
 */

import { TEXT_LOCALES } from './localizedText.js';
import type { GuideHours, ImageQuality, PlayerSettings } from './settings.js';

/** Le voci del telefono che girano fra più valori invece di accendersi e spegnersi. */
export type PhoneCycleKey = 'imageQuality' | 'guideHours' | 'locale';

const IMAGE_QUALITIES: ReadonlyArray<ImageQuality> = ['low', 'medium', 'high'];

/**
 * Tre, sei o ventiquattro ore di guida.
 *
 * Non è una preferenza estetica: è quanto si chiede al gateway e quanto si tiene in memoria, e su un
 * apparecchio lento le ventiquattro ore si pagano.
 */
const GUIDE_HOURS: ReadonlyArray<GuideHours> = [3, 6, 24];

const LOCALES: ReadonlyArray<PlayerSettings['locale']> = ['auto', ...TEXT_LOCALES];

/**
 * Il valore successivo di una voce, tornando al primo dopo l'ultimo.
 *
 * Un valore che **non** è nell'elenco — arrivato da un archivio scritto da una versione diversa
 * dell'app — riparte dal primo invece di restare fermo: fermo sarebbe un comando che non risponde,
 * e chi tocca una riga che non cambia niente conclude che l'app è rotta.
 */
export function nextSettingValue(
  settings: PlayerSettings,
  key: PhoneCycleKey,
): PlayerSettings[PhoneCycleKey] {
  if (key === 'imageQuality') return successivo(IMAGE_QUALITIES, settings.imageQuality);
  if (key === 'guideHours') return successivo(GUIDE_HOURS, settings.guideHours);
  return successivo(LOCALES, settings.locale);
}

function successivo<T>(valori: ReadonlyArray<T>, corrente: T): T {
  const dove = valori.indexOf(corrente);
  // `-1 + 1 = 0`: lo sconosciuto riparte dal primo, senza un ramo apposta.
  return valori[(dove + 1) % valori.length] as T;
}
